/**
 * TenantAlertsConfig — per-tenant real-time alert preferences.
 *
 * Each school can configure its own Slack/Teams Incoming Webhook so tenant-scoped
 * alerts (safeguarding, behaviour, support) fire into THEIR channel, not just the
 * platform super-admin webhook. Also toggles the minimum severity threshold.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Radio, Send, Save } from 'lucide-react';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const token = getToken();
  return { Authorization: `Bearer ${token}` };
};

export const TenantAlertsConfig = ({ tenantId }) => {
  const [form, setForm] = useState({ webhook_url: '', enabled: false, min_severity: 'high' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    axios.get(`${API_URL}/api/school/tenants/${tenantId}/alerts`, { headers: authHeaders() })
      .then((r) => setForm({ ...form, ...r.data }))
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/school/tenants/${tenantId}/alerts`, form, { headers: authHeaders() });
      toast.success('Alert preferences saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const test = async () => {
    try {
      const r = await axios.post(`${API_URL}/api/school/tenants/${tenantId}/alerts/test`, {}, { headers: authHeaders() });
      if (r.data?.webhook_configured && r.data?.enabled) {
        toast.success('Test alert fired — check your Slack/Teams channel');
      } else {
        toast.info('Preview sent to live feed. Enable & paste a webhook URL to also route to Slack/Teams.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test failed');
    }
  };

  return (
    <Card className="border-slate-200" data-testid="tenant-alerts-config-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Radio className="h-4 w-4 text-emerald-500" /> Real-time Alerts
        </CardTitle>
        <CardDescription>
          Pipe this school's safeguarding, behaviour, and support alerts into your own Slack or Microsoft Teams channel.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <label className="flex items-center gap-3 text-sm">
          <Switch
            checked={!!form.enabled}
            onCheckedChange={(v) => setForm({ ...form, enabled: !!v })}
            data-testid="tenant-alerts-enabled"
          />
          <span>Enable Slack / Teams webhook alerts for this school</span>
        </label>
        <div>
          <Label className="text-xs">Incoming Webhook URL</Label>
          <Input
            value={form.webhook_url || ''}
            onChange={(e) => setForm({ ...form, webhook_url: e.target.value })}
            placeholder="https://hooks.slack.com/services/…"
            className="font-mono text-xs"
            data-testid="tenant-alerts-webhook-url"
          />
          <p className="text-[11px] text-slate-500 mt-1">
            Slack → Apps → Incoming Webhooks → Add New Webhook. For Teams: Channel → Connectors → Incoming Webhook.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Minimum Severity</Label>
            <Select
              value={form.min_severity || 'high'}
              onValueChange={(v) => setForm({ ...form, min_severity: v })}
            >
              <SelectTrigger data-testid="tenant-alerts-min-severity"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['low', 'medium', 'high', 'critical'].map((s) => (
                  <SelectItem key={s} value={s}>{s.toUpperCase()}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-2">
            <Button type="button" variant="outline" onClick={test} data-testid="tenant-alerts-test-btn">
              <Send className="h-4 w-4 mr-2" /> Send test
            </Button>
            <Button type="button" onClick={save} disabled={saving || loading} data-testid="tenant-alerts-save-btn">
              <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TenantAlertsConfig;
